import {Component, inject, input, OnInit, output, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {ModelsService} from '../../../../services/models.service';
import {AssetsService} from '../../../../services/assets.service';
import {FormControl, FormGroup} from '@angular/forms';
import {UserStorageService} from "../../../../services/user-storage.service";
import {ToastService} from '../../../../services/toast.service';
import {distinctUntilChanged} from "rxjs";

@Component({
  selector: 'service-request-asset-component',
  templateUrl: './service-request-asset.component.html',
  styleUrls: ['./service-request-asset.component.css'],
  standalone: false
})
export class ServiceRequestAssetComponent implements OnInit {
  initAsset = input.required<any>();
  assetIndex = input.required<number>();
  assetsCount = input<any>();
  locationId = input<any>();
  parentForm = input<FormGroup>();
  deleteAsset = output<number>();

  asset: any;
  searchedAssets: any[] = [];
  searchedModels: any[] = [];
  searchedMakes: any[] = [];
  searchedTypes: any[] = [];
  modalOpen: boolean = false;
  modalImageUrl: string = "";
  assetsControl = new FormControl();
  modelsControl = new FormControl();
  makesControl = new FormControl();
  typesControl = new FormControl();
  issueControl = new FormControl('');

  private readonly modelsService = inject(ModelsService);
  private readonly assetsService = inject(AssetsService);
  private readonly userStorageService = inject(UserStorageService);
  private readonly toastService = inject(ToastService);
  private readonly destroyRef = inject(DestroyRef);

  ngOnInit() {
    this.asset = this.initAsset();
    if (!this.asset.is_new) {
      this.searchedAssets = [{...this.asset}];
      this.searchedModels = [{
        model_id: this.asset.model_id,
        model_name: this.asset.model_name,
        make_id: this.asset.make_id,
        make_name: this.asset.make_name,
        type_id: this.asset.type_id,
        type_name: this.asset.type_name,
      }];
      this.searchedMakes = [{
        make_id: this.asset.make_id,
        make_name: this.asset.make_name
      }];
      this.searchedTypes = [{
        type_id: this.asset.type_id,
        type_name: this.asset.type_name
      }];
      this.assetsControl.setValue(this.asset.asset_id);
      this.modelsControl.setValue(this.asset.model_id);
      this.makesControl.setValue(this.asset.make_id);
      this.typesControl.setValue(this.asset.type_id);
    }
    this.parentForm()?.addControl('assetsControl' + this.assetIndex(), this.assetsControl);
    this.parentForm()?.addControl('modelsControl' + this.assetIndex(), this.modelsControl);
    this.parentForm()?.addControl('makesControl' + this.assetIndex(), this.makesControl);
    this.parentForm()?.addControl('typesControl' + this.assetIndex(), this.typesControl);
    this.parentForm()?.addControl('issueControl' + this.assetIndex(), this.issueControl);

    this.asset.pm_contract = 2;
    this.asset.service_contract = 2;
    this.asset.under_warranty = 2;

    this.assetsControl.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const newValue = this.assetsControl.value;
      this.asset.asset_id = newValue;
      if (newValue) {
        const selectedAsset = this.searchedAssets.find((asset: any) => asset.asset_id === newValue);
        this.asset.serial_number = selectedAsset.serial_number;
        this.asset.is_sn_unknown = selectedAsset.is_sn_unknown;

        // Check if asset was just created
        if (newValue === -1) return;

        // Ensure that model is displayed properly in select
        if (!this.searchedModels.some((model: any) => model.model_id == selectedAsset.model_id)) {
          this.searchedModels = [...this.searchedModels, {
            model_id: selectedAsset.model_id,
            model_name: selectedAsset.model_name,
            make_id: selectedAsset.make_id,
            make_name: selectedAsset.make_name,
            type_id: selectedAsset.type_id,
            type_name: selectedAsset.type_name,
          }];
        }
        this.modelsControl.setValue(selectedAsset.model_id);
      } else {
        this.asset.serial_number = undefined;
        this.asset.is_sn_unknown = false;
      }
    });
    this.modelsControl.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const newValue = this.modelsControl.value;
      this.asset.model_id = newValue;
      if (newValue) {
        const selectedModel = this.searchedModels.find((model: any) => model.model_id === newValue);
        this.asset.model_name = selectedModel.model_name;

        // Check if model was just created
        if (newValue === -1) return;

        // Ensure that make is displayed properly in select
        if (!this.searchedMakes.some((make: any) => make.make_id == selectedModel.make_id)) {
          this.searchedMakes = [...this.searchedMakes, {
            make_id: selectedModel.make_id,
            make_name: selectedModel.make_name,
          }];
        }
        // Ensure that type is displayed properly in select
        if (!this.searchedTypes.some((type: any) => type.type_id == selectedModel.type_id)) {
          this.searchedTypes = [...this.searchedTypes, {
            type_id: selectedModel.type_id,
            type_name: selectedModel.type_name,
          }];
        }
        this.makesControl.setValue(selectedModel.make_id);
        this.typesControl.setValue(selectedModel.type_id);
      } else {
        this.asset.model_name = undefined;
      }
    });
    this.makesControl.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const newValue = this.makesControl.value;
      this.asset.make_id = newValue;
      if (newValue) {
        const selectedMake = this.searchedMakes.find((make: any) => make.make_id === newValue);
        this.asset.make_name = selectedMake.make_name;
      } else {
        this.asset.make_name = undefined;
      }
    });
    this.typesControl.valueChanges.pipe(
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      const newValue = this.typesControl.value;
      this.asset.type_id = newValue;
      if (newValue) {
        const selectedType = this.searchedTypes.find((type: any) => type.type_id === newValue);
        this.asset.type_name = selectedType.type_name;
      } else {
        this.asset.type_name = undefined;
      }
    });
  }

  onPhotoSelected(event: any, asset_index: any): void {
    const files: FileList = event.target.files;
    const maxSize = 40 * 1024 * 1024;  // MAX size 40MB

    for (let i = 0; i < files.length; i++) {
      const file = files[i];

      if (file.size > maxSize) {
        this.toastService.showToast("File is too large. Please select a file smaller than 40 MB", "error");
        event.target.value = '';
        return;
      }

      const reader = new FileReader();

      reader.onload = ((file) => {
        return (e: any) => {
          if (!this.asset.attachments) {
            this.asset.attachments = [];
          }
          this.asset.attachments.push({
            url: e.target.result,
            fileName: file.name,
            service_request_asset_number: asset_index
          });
        };
      })(file);

      reader.readAsDataURL(file);
    }
  }

  showFullPhoto(attachment: any) {
    this.modalImageUrl = attachment.url;
    this.modalOpen = true;
  }

  closeModal(event: MouseEvent): void {
    const isModalClicked = event.target === event.currentTarget;
    if (isModalClicked) {
      this.modalOpen = false;
      this.modalImageUrl = "";
    }
  }

  isSupportedFileType(fileName: string): boolean { // Check photo type for correct displayed
    const supportedFileTypes = ['jpg', 'jpeg', 'png', 'gif', 'bmp', 'webp', 'svg', 'tiff', 'psd', 'ai', 'eps'];
    const fileExtension = fileName.split('.').pop()?.toLowerCase();
    return !!fileExtension && supportedFileTypes.includes(fileExtension);
  }

  removePhoto(index: number): void {
    this.asset.attachments.splice(index, 1);
  }

  addCustomAsset = (newAssetName: string) => {
    newAssetName = newAssetName.trim();
    const newCustomAsset = {
      asset_id: -1,
      serial_number: newAssetName,
      is_sn_unknown: false
    }
    this.searchedAssets = [newCustomAsset, ...this.searchedAssets.filter((asset: any) => asset.asset_id == -1)];
    return newCustomAsset;
  };

  addCustomModel = (newModelName: string) => {
    newModelName = newModelName.trim();
    const newCustomModel = {
      model_id: -1,
      model_name: newModelName
    }
    this.searchedModels = [newCustomModel, ...this.searchedModels.filter((model: any) => model.model_id == -1)];
    return newCustomModel;
  };

  addCustomMake = (newMakeName: string) => {
    newMakeName = newMakeName.trim();
    const newCustomMake = {
      make_id: -1,
      make_name: newMakeName
    }
    this.searchedMakes = [newCustomMake, ...this.searchedMakes.filter((make: any) => make.make_id == -1)];
    return newCustomMake;
  };

  addCustomType = (newTypeName: string) => {
    newTypeName = newTypeName.trim();
    const newCustomType = {
      type_id: -1,
      type_name: newTypeName
    }
    this.searchedTypes = [newCustomType, ...this.searchedTypes.filter((type: any) => type.type_id == -1)];
    return newCustomType;
  };

  onAssetSearch(event: { term: string; items: any[] }) {
    if (event.term.length >= 3) {
      this.assetsService.getAssetsByLookup(this.locationId(), event.term).then((data: any) => {
        this.searchedAssets = Array.isArray(data) ? data : [];
      });
    } else {
      this.searchedAssets = [];
    }
  }

  onModelSearch(event: { term: string; items: any[] }) {
    if (event.term.length >= 3) {
      this.modelsService.getModelsByLookup(event.term, "0").then((data: any) => {
        this.searchedModels = Array.isArray(data?.data) ? data.data : [];
      });
    } else {
      this.searchedModels = [];
    }
  }

  onMakeSearch(event: { term: string; items: any[] }) {
    if (event.term.length >= 3) {
      this.modelsService.getMakesByLookup(event.term, "0").then((data: any) => {
        this.searchedMakes = Array.isArray(data?.data) ? data.data : [];
      });
    } else {
      this.searchedMakes = [];
    }
  }

  onTypeSearch(event: { term: string; items: any[] }) {
    if (event.term.length >= 3) {
      this.modelsService.getTypesByLookup(event.term, "0").then((data: any) => {
        this.searchedTypes = Array.isArray(data?.data) ? data.data : [];
      });
    } else {
      this.searchedTypes = [];
    }
  }

  showAll(_term: string, _item: any) {
    return true;
  }

  onAssetClear() {
    this.searchedAssets = [];
  }

  onModelClear() {
    this.searchedModels = [];
  }

  onMakeClear() {
    this.searchedMakes = [];
  }

  onTypeClear() {
    this.searchedTypes = [];
  }

  onDeleteAsset() {
    this.parentForm()?.removeControl('assetsControl' + this.assetIndex());
    this.parentForm()?.removeControl('modelsControl' + this.assetIndex());
    this.parentForm()?.removeControl('makesControl' + this.assetIndex());
    this.parentForm()?.removeControl('typesControl' + this.assetIndex());
    this.parentForm()?.removeControl('issueControl' + this.assetIndex());

    this.deleteAsset.emit(this.assetIndex());
    this.userStorageService.removeFromLocalStorageArray("serviceRequest", this.asset)
  }
}
